/**
 * Loads theme custom-element modules only when their markup is close to the viewport.
 *
 * Components that control hidden global UI (dialogs, localization, popovers) stay eager.
 * Everything else is observed with a generous root margin so it is upgraded before the
 * customer reaches it without making below-the-fold modules compete with the first paint.
 */

const componentModules = new Map([
  ['quick-add-component', ['@theme/quick-add']],
  ['gift-card-recipient-form', ['@theme/gift-card-recipient-form']],
  ['cart-discount-component', ['@theme/cart-discount']],
  ['dialog-component', ['@theme/dialog']],
  ['variant-picker', ['@theme/variant-picker']],
  ['product-card', ['@theme/product-card']],
  ['product-card-link', ['@theme/product-card']],
  ['swatches-variant-picker-component', ['@theme/product-card']],
  ['add-to-cart-component', ['@theme/product-form', '@theme/fly-to-cart']],
  ['product-form-component', ['@theme/product-form', '@theme/fly-to-cart']],
  ['accordion-custom', ['@theme/accordion-custom']],
  ['deferred-media', ['@theme/media']],
  ['product-model', ['@theme/media']],
  ['product-price', ['@theme/product-price']],
  ['product-sku-component', ['@theme/product-sku']],
  ['product-title', ['@theme/product-title']],
  ['product-inventory', ['@theme/product-inventory']],
  ['show-more-component', ['@theme/show-more']],
  ['slideshow-component', ['@theme/slideshow']],
  ['layered-slideshow-component', ['@theme/layered-slideshow']],
  ['anchored-popover-component', ['@theme/anchored-popover']],
  ['floating-panel-component', ['@theme/floating-panel']],
  ['video-background-component', ['@theme/video-background']],
  ['hero-banner-component', ['@theme/hero-banner']],
  ['quantity-selector-component', ['@theme/component-quantity-selector']],
  ['media-gallery', ['@theme/media-gallery']],
  ['rte-formatter', ['@theme/rte-formatter']],
  ['volume-pricing', ['@theme/volume-pricing']],
  ['price-per-item', ['@theme/price-per-item']],
  ['volume-pricing-info', ['@theme/volume-pricing-info']],
  ['localization-form-component', ['@theme/localization']],
  ['dropdown-localization-component', ['@theme/localization']],
  ['drawer-localization-component', ['@theme/localization']],
  ['sticky-add-to-cart', ['@theme/sticky-add-to-cart']],
]);

const sectionModules = new Map([
  ['text-scroll-reveal', [{ specifier: '@theme/looki-section-components', initializer: 'initLookiSection' }]],
  ['story-card', [{ specifier: '@theme/looki-section-components', initializer: 'initLookiSection' }]],
  ['media-endorsement', [{ specifier: '@theme/looki-section-components', initializer: 'initLookiSection' }]],
  ['social-proof', [{ specifier: '@theme/looki-section-components', initializer: 'initLookiSection' }]],
  ['how-looki-works', [{ specifier: '@theme/looki-section-components', initializer: 'initLookiSection' }]],
  ['story-telling', [{ specifier: '@theme/looki-section-components', initializer: 'initLookiSection' }]],
  ['expandable-stories', [{ specifier: '@theme/looki-expandable-stories', initializer: 'initExpandableStories' }]],
]);

const eagerComponents = new Set([
  'cart-discount-component',
  'dialog-component',
  'gift-card-recipient-form',
  'anchored-popover-component',
  'floating-panel-component',
  'localization-form-component',
  'dropdown-localization-component',
  'drawer-localization-component',
  'sticky-add-to-cart',
]);

const componentSelector = [...componentModules.keys(), '[data-looki-section]'].join(',');
const requestedModules = new Map();
const scheduledElements = new WeakSet();

const requestModule = (specifier) => {
  if (!requestedModules.has(specifier)) {
    requestedModules.set(
      specifier,
      import(specifier).catch((error) => {
        requestedModules.delete(specifier);
        console.error(`Unable to load theme component module: ${specifier}`, error);
        return null;
      })
    );
  }

  return requestedModules.get(specifier);
};

const loadComponent = (element) => {
  const customElementSpecifiers = componentModules.get(element.localName);
  const sectionSpecifiers = sectionModules.get(element.dataset.lookiSection);
  const moduleRequests = sectionSpecifiers || customElementSpecifiers?.map((specifier) => ({ specifier }));
  if (!moduleRequests || (customElementSpecifiers && customElements.get(element.localName))) return;

  viewportObserver?.unobserve(element);
  moduleRequests.forEach(({ specifier, initializer }) => {
    requestModule(specifier).then((module) => {
      if (initializer && module) module[initializer]?.(element);
    });
  });
};

const viewportObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) loadComponent(entry.target);
          });
        },
        { rootMargin: '1000px 0px', threshold: 0 }
      )
    : null;

const scheduleComponent = (element) => {
  if (scheduledElements.has(element) || customElements.get(element.localName)) return;
  scheduledElements.add(element);

  if (eagerComponents.has(element.localName) || !viewportObserver) {
    loadComponent(element);
    return;
  }

  viewportObserver.observe(element);
};

const discoverComponents = (root) => {
  if (
    root instanceof Element &&
    (componentModules.has(root.localName) || sectionModules.has(root.dataset.lookiSection))
  ) {
    scheduleComponent(root);
  }

  root.querySelectorAll?.(componentSelector).forEach(scheduleComponent);
};

// Pre-warm an observed component immediately when a customer starts interacting with it.
['pointerdown', 'focusin'].forEach((eventName) => {
  document.addEventListener(
    eventName,
    (event) => {
      const target = event.target instanceof Element ? event.target.closest(componentSelector) : null;
      if (target) loadComponent(target);
    },
    { capture: true, passive: eventName === 'pointerdown' }
  );
});

const observeComponentChanges = (root) => {
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) discoverComponents(node);
      });
    });
  }).observe(root, { childList: true, subtree: true });
};

// Header and hidden global UI must be ready immediately, but they are small bounded roots.
[
  document.querySelector('#header-group'),
  document.querySelector('#search-modal'),
  document.querySelector('#quick-add-dialog'),
]
  .filter(Boolean)
  .forEach((root) => {
    discoverComponents(root);
    observeComponentChanges(root);
  });

// Main-content sections and the footer are scanned only when they approach the viewport.
// This avoids the previous full-document query and keeps below-the-fold component discovery
// off the critical path.
const discoveryRootObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            discoveryRootObserver.unobserve(entry.target);
            discoverComponents(entry.target);
            observeComponentChanges(entry.target);
          });
        },
        { rootMargin: '1200px 0px', threshold: 0 }
      )
    : null;

const scheduleDiscoveryRoot = (root) => {
  if (!(root instanceof Element)) return;
  if (discoveryRootObserver) discoveryRootObserver.observe(root);
  else {
    discoverComponents(root);
    observeComponentChanges(root);
  }
};

const mainContent = document.querySelector('#MainContent');
if (mainContent) {
  Array.from(mainContent.children).forEach(scheduleDiscoveryRoot);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(scheduleDiscoveryRoot);
    });
  }).observe(mainContent, { childList: true });
}

scheduleDiscoveryRoot(document.querySelector('footer'));

['shopify:section:load', 'shopify:section:rerender'].forEach((eventName) => {
  document.addEventListener(eventName, (event) => {
    if (event.target instanceof Element) discoverComponents(event.target);
  });
});
